/**
 * AcctSubPage — page d'une tuile comptable, pour les deux portées (Batch #14.274) :
 *   scope « books »   : /admin/tenue-livres/:masterId/:tileId   (API /books/{masterId})
 *   scope « cabinet » : /admin/comptabilite/:tileId              (API /gl)
 * Tuiles : clients (books · au cabinet = n° de client des dossiers Master) · fournisseurs · transactions-clients · transactions-fournisseurs ·
 * grand-livre · rapports · plan · produits.
 * Report en deux étapes : « Reporter » (auxiliaire) dans les Transactions ; « Reporter au grand livre » dans Grand livre › Journal général.
 */
import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Calculator, Hash, Landmark, Loader2 } from "lucide-react";
import { api } from "@/api";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { GlBaseProvider, useAccounts } from "../comptabilite/_shared";
import ChartOfAccountsTab from "../comptabilite/ChartOfAccountsTab";
import ClientsTab from "../comptabilite/ClientsTab";
import EntriesTab from "../comptabilite/EntriesTab";
import LedgerTab from "../comptabilite/LedgerTab";
import ReportsTab from "../comptabilite/ReportsTab";
import { BOOKS_TILES, LEGACY_TILES, SCOPES, TILE_TABS } from "./_books";
import BankTab from "./bank/BankTab";
import PartiesTab from "./PartiesTab";
import ProductsTab from "./ProductsTab";
import DocsTab from "./DocsTab";
import PaymentsTab from "./PaymentsTab";
import LegacyImportBanner from "./LegacyImportBanner";
import ReconcileTab from "./ReconcileTab";
import AssetsTab from "./assets/AssetsTab";
import InstalmentsTab from "./InstalmentsTab";
import CloseTab from "./close/CloseTab";
import QboTile from "./qbo/QboTile";
import EntrepreneurTileContent from "../entrepreneurs/EntrepreneurTileContent";
import { ENT_INBOX_REDIRECT, ENT_TILE_IDS } from "../entrepreneurs/_shared";

const ACTIVE = { teal: "border-teal-600 text-teal-700", emerald: "border-emerald-600 text-emerald-700" };
const CABINET_CLIENTS_TILE = { id: "clients", title: "N° de client", icon: Hash };

function Tabs({ tabs, tab, setTab, drafts, color }) {
  return (
    <div className="flex gap-1 border-b overflow-x-auto">
      {tabs.map((t) => {
        const nb = t.badge ? drafts?.[t.badge] || 0 : 0;
        return (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm border-b-2 transition whitespace-nowrap inline-flex items-center gap-1.5 ${tab === t.id ? `${ACTIVE[color]} font-semibold` : "border-transparent text-gray-500 hover:text-gray-700"}`}
            data-testid={`acct-tab-${t.id}`}>
            {t.label}{nb > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-800" title="Non reporté(s)" data-testid={`acct-tab-badge-${t.id}`}>{nb}</span>}
          </button>
        );
      })}
    </div>
  );
}

function TileContent({ tileId, scope, common, color }) {
  const [sp, setSp] = useSearchParams();
  const [reloadKey, setReloadKey] = useState(0);
  const tabs = TILE_TABS[tileId];
  const tab = tabs && tabs.some((t) => t.id === sp.get("tab")) ? sp.get("tab") : tabs?.[0]?.id;
  const setTab = (id) => { const n = new URLSearchParams(sp); if (id === tabs[0].id) n.delete("tab"); else n.set("tab", id); n.delete("report"); n.delete("party"); setSp(n, { replace: true }); };
  const drafts = common.settings?.drafts || {};
  if (tileId === "clients") return scope === "cabinet" ? <ClientsTab {...common} /> : <PartiesTab kind="customer" {...common} />;
  if (tileId === "fournisseurs") return <PartiesTab kind="supplier" {...common} />;
  if (tileId === "produits") return <ProductsTab {...common} />;
  if (tileId === "banque") return <BankTab {...common} />;
  if (tileId === "concordance") return <ReconcileTab {...common} />;
  if (tileId === "immobilisations") return <AssetsTab {...common} />;
  if (tileId === "cloture") return <CloseTab {...common} />;
  if (tileId === "acomptes") return <InstalmentsTab {...common} />;
  if (ENT_TILE_IDS.includes(tileId)) return <EntrepreneurTileContent masterId={common.masterId} tileId={tileId} />;
  if (tileId === "rapports") return <ReportsTab {...common} />;
  if (tileId === "plan") return <ChartOfAccountsTab {...common} />;
  if (tileId === "quickbooks") {
    return (
      <div className="space-y-4" data-testid="acct-tile-tabs-quickbooks">
        <Tabs tabs={tabs} tab={tab} setTab={setTab} drafts={drafts} color={color} />
        <QboTile {...common} tab={tab} />
      </div>
    );
  }
  const customer = tileId === "transactions-clients";
  return (
    <div className="space-y-4" data-testid={`acct-tile-tabs-${tileId}`}>
      {scope === "cabinet" && tileId !== "grand-livre" && <LegacyImportBanner kind={customer ? "customer" : "supplier"} refresh={() => { common.refresh(); setReloadKey((k) => k + 1); }} />}
      <Tabs tabs={tabs} tab={tab} setTab={setTab} drafts={drafts} color={color} />
      <div key={reloadKey} className="space-y-4">
      {tileId === "grand-livre" && tab === "journal" && <EntriesTab {...common} />}
      {tileId === "grand-livre" && tab === "grand-livre" && <LedgerTab {...common} />}
      {tileId !== "grand-livre" && (tab === "ventes" || tab === "achats") && <DocsTab kind={customer ? "sale" : "purchase"} {...common} />}
      {tileId !== "grand-livre" && tab === "notes-credit" && <DocsTab kind="sale_credit" {...common} />}
      {tileId !== "grand-livre" && tab === "notes-debit" && <DocsTab kind="purchase_credit" {...common} />}
      {tileId !== "grand-livre" && (tab === "encaissements" || tab === "decaissements") && <PaymentsTab kind={customer ? "receipt" : "disbursement"} {...common} />}
      {tileId !== "grand-livre" && tab === "journal" && <EntriesTab {...common} journals={customer ? ["VEN", "ENC"] : ["ACH", "DEC"]} readOnly />}
      </div>
    </div>
  );
}

function Loaded({ scope, masterId, tileId, settings, refresh }) {
  const navigate = useNavigate();
  const { accounts, reload: reloadAccounts } = useAccounts();
  const sc = SCOPES[scope];
  const pathFor = (tile, tab) => { const legacy = LEGACY_TILES[tile]; return legacy ? sc.path(masterId, legacy[0], tab || legacy[1]) : sc.path(masterId, tile, tab); };
  const common = { accounts, settings, masterId, refresh: () => { refresh(); reloadAccounts(); }, goTab: (tile, tab) => navigate(pathFor(tile, tab)), pathFor, scope };
  return <TileContent tileId={tileId} scope={scope} common={common} color={sc.color} />;
}

export default function AcctSubPage({ scope }) {
  const { masterId, tileId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const sc = SCOPES[scope];
  const base = sc.base(masterId);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([api.get(`${base}/settings`), api.get(`${base}/overview`).catch(() => ({ data: {} }))]);
      setSettings({ ...s.data, drafts: o.data?.drafts || {} }); setError(false);
    } catch { setError(true); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const tile = scope === "cabinet" && tileId === "clients" ? CABINET_CLIENTS_TILE : BOOKS_TILES.find((t) => t.id === tileId);
  const { setTrail } = useBreadcrumb();
  useEffect(() => {
    if (!tile) return undefined;
    const tabLabel = TILE_TABS[tileId]?.find((t) => t.id === (sp.get("tab") || TILE_TABS[tileId][0].id))?.label;
    const head = scope === "books" ? [{ label: sc.hubLabel, href: sc.hubHref }, { label: settings?.legal_name || "Dossier", href: sc.hub(masterId) }] : [{ label: sc.hubLabel, href: sc.hubHref }];
    setTrail([...head, tabLabel ? { label: tile.title, href: sc.path(masterId, tileId) } : { label: tile.title }, ...(tabLabel ? [{ label: tabLabel }] : [])]);
    return () => setTrail(null);
  }, [tile, tileId, settings, masterId, setTrail, sp, sc, scope]);

  if (!tile && LEGACY_TILES[tileId]) {
    const [t, tab] = LEGACY_TILES[tileId];
    return <Navigate to={sc.path(masterId, t, tab)} replace />;
  }
  // Batch #14.314 — anciennes tuiles Revenus / Dépenses / Kilométrage → Files PMS › Entreprise
  if (!tile && scope === "books" && ENT_INBOX_REDIRECT[tileId]) return <Navigate to={`/admin/pms-queue?tab=${ENT_INBOX_REDIRECT[tileId]}`} replace />;

  if (!tile || error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white border-2 border-rose-200 rounded-xl p-8 text-center">
          <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h2 className="text-base font-bold text-slate-800">{!tile ? "Module inconnu" : "Dossier inaccessible"}</h2>
          <button onClick={() => navigate(sc.hubHref)} className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour
          </button>
        </div>
      </div>
    );
  }

  const Icon = scope === "books" ? Calculator : Landmark;
  return (
    <GlBaseProvider value={base}>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid={`books-tile-${tileId}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate(sc.hub(masterId))} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition active:scale-90"
            aria-label="Retour" data-testid="books-tile-back-btn">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex items-center gap-2 text-sm text-slate-500 flex-1 min-w-0">
            <Icon className={`w-4 h-4 shrink-0 ${scope === "books" ? "text-teal-600" : "text-emerald-600"}`} />
            <span className="font-medium truncate hidden sm:inline" data-testid="books-tile-entity">{settings?.legal_name || (scope === "books" ? "Tenue de livres" : "Comptabilité du cabinet")}</span>
            <span className="text-slate-300 hidden sm:inline">›</span>
            <span className="font-bold text-slate-800 truncate">{tile.title}</span>
          </div>
          {settings && (
            <div className="flex gap-3 text-xs text-slate-500 w-full sm:w-auto pl-12 sm:pl-0 -mt-2 sm:mt-0 flex-wrap" data-testid="books-kpis">
              <span><strong className="text-slate-800" data-testid="books-kpi-nb-accounts">{settings.nb_accounts ?? 0}</strong> comptes</span>
              <span><strong className="text-slate-800" data-testid="books-kpi-nb-entries">{settings.nb_entries ?? 0}</strong> écritures</span>
              {settings.nb_draft_entries > 0 && <span className="text-orange-700" data-testid="books-kpi-nb-draft">{settings.nb_draft_entries} non reportée(s) au GL</span>}
              {settings.locked_until && <span className="text-amber-700">Verrouillé jusqu'au {settings.locked_until}</span>}
            </div>
          )}
          {!settings && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>
        {settings && <Loaded scope={scope} masterId={masterId} tileId={tileId} settings={settings} refresh={load} />}
      </div>
    </GlBaseProvider>
  );
}
